import { useCallback, useMemo, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  EVENT_STEP_ORDER,
  EventChatActivitiesPicker,
  EventChatInsumosPicker,
  EventChatLocationPicker,
  EventChatMembersPicker,
  EventChatTypePicker,
  EventFormChatSummaryCard,
  buildMergedInsumos,
  buildMergedMediums,
  type EventChatLocationState,
  type EventChatMembersConfirmPayload,
  type EventChatSummary,
  type EventMergedInsumoOption,
  type EventMergedMediumOption,
  type EventStructuredSelectionPayload,
  type FormChatEditableFieldOption,
} from "gsintegrations";
import { trackEventsError } from "../analytics/events";
import { useSubscription } from "../auth/SubscriptionContext";
import { useCachedTerreiro } from "../hooks/useCachedTerreiro";
import { useCreateEventQueries } from "../hooks/useCreateEventQueries";
import { useFormChatSubmitEvent } from "../hooks/useFormChatSubmitEvent";
import { useProductAnalyticsBase } from "../hooks/useProductAnalyticsContext";
import type {
  EventReferenceData,
  FormChatMessageRequest,
  FormChatMessageResponse,
  FormChatState,
} from "../repositories/formChat.repository";
import { formChatRepository } from "../repositories/formChat.repository";
import { isTerreiroAddressConfigured } from "../utils/eventLocationAddress";
import {
  formatParticipantsSelectionEcho,
  quorumForMemberSelection,
} from "../utils/formChatMembersSelection";
import { extractAxiosApiError, toSafeInventoryQuantity } from "../utils/extractAxiosApiError";
import {
  buildEventFormChatPayload,
  normalizeEventFormChatState,
} from "../utils/eventFormChatPayloadBuilder";
import {
  CHECKIN_WINDOW_EDIT_FIELDS,
  formatChatDate,
  formatChatMinutes,
  formatChatText,
  formatChatTime,
  formatChatToggle,
  formatChatYesNo,
  toInsumoSelection,
  toStringList,
} from "../utils/formChatEditPreview";

const EVENT_TYPE_OPTIONS = [
  { value: "GIRA_ATENDIMENTO", label: "Gira (Atendimento)" },
  { value: "GIRA_DESENVOLVIMENTO", label: "Gira de Desenvolvimento (Médiuns)" },
  { value: "GIRA_FECHADA", label: "Reunião de Médiuns / Gira Fechada (Interna)" },
  { value: "REUNIAO_ADMINISTRATIVA", label: "Reunião de Diretoria/Administração" },
  { value: "ORACULO", label: "Sessão de Estudos / Escola" },
  { value: "OFERENDAS", label: "Obrigações / Rito Interno" },
  { value: "MANUTENCAO", label: "Limpeza / Defumação da Casa" },
  { value: "FESTA", label: "Festa / Homenagem (Orixá/Entidade)" },
  { value: "ATENDIMENTO_PARTICULAR", label: "Bori / Preceito (quando aplicável)" },
  {
    value: "CONFRATERNIZACAO",
    label: "Trabalhos de Esquerda (Quimbanda) / Sessão de Exu-Pombagira (se aplicável)",
  },
  { value: "REFORMA", label: "Mutirão / Manutenção do Terreiro" },
  { value: "ACAO_SOLIDARIA", label: "Caridade / Ação Social" },
  { value: "GIRA_ABERTA", label: "Gira Aberta (Pública)" },
  { value: "OTHER", label: "Outro (com campo de texto opcional)" },
];

const formatLocationLabel = (location?: EventChatLocationState): string => {
  if (!location || location.isInTerreiro !== false) {
    return "No terreiro";
  }
  return [location.street, location.number, location.neighborhood, location.city, location.state]
    .filter(Boolean)
    .join(", ");
};

const buildStructuredSelectionLabel = (
  field: EventStructuredSelectionPayload["field"],
  value: unknown,
): string => {
  if (field === "type") {
    const typeId = value as string;
    return (
      EVENT_TYPE_OPTIONS.find((option) => option.value === typeId)?.label ?? typeId
    );
  }
  if (field === "location") {
    return formatLocationLabel(value as EventChatLocationState);
  }
  if (field === "activities") {
    const payload = value as {
      activities: Array<{ id: string }>;
    };
    const count = payload.activities.length;
    return count > 0 ? `${count} atividade(s) selecionada(s)` : "Sem atividades";
  }
  if (field === "memberIds") {
    const payload = value as EventChatMembersConfirmPayload;
    return formatParticipantsSelectionEcho(
      payload.memberIds.length,
      quorumForMemberSelection(payload.memberIds, payload.minConfirmedMembers),
    );
  }
  if (field === "insumos") {
    const items = value as Array<{ id: string; quantity: number }>;
    return items.length > 0
      ? `Selecionados ${items.length} material(is)`
      : "Sem materiais";
  }
  return "";
};

const EVENT_EDITABLE_FIELDS: Array<{ key: string; label: string }> = [
  { key: "name", label: "Nome" },
  { key: "type", label: "Tipo" },
  { key: "description", label: "Descrição" },
  { key: "isTemplate", label: "Modelo" },
  { key: "date", label: "Data" },
  { key: "startTime", label: "Horário início" },
  { key: "endTime", label: "Horário término" },
  { key: "location", label: "Local" },
  { key: "qrCheckinEnabled", label: "Check-in" },
  { key: "checkinStartWindowMinutes", label: "Check-in antes (min)" },
  { key: "checkinEndWindowMinutes", label: "Check-in depois (min)" },
  { key: "activities", label: "Atividades" },
  { key: "memberIds", label: "Participantes" },
  { key: "minConfirmedMembers", label: "Quórum mínimo" },
  { key: "insumos", label: "Materiais" },
];

interface UseEventFormChatOptions {
  sessionId?: string;
  activeField?: string;
  formState: FormChatState;
  summary?: EventChatSummary;
  descriptionAutoGenerated?: boolean;
  onResponse: (response: FormChatMessageResponse) => void;
  onUserMessage: (content: string) => void;
  onError?: (error: { code: string; message: string }) => void;
  onReset: () => void;
  onEdit?: () => void;
  onCloseAssistant?: () => void;
  /** Event model the new event is created from. */
  modelTargetId?: string;
  /** Fetch inventory, médiuns, MODEL activities and address only while the flow is open. */
  enabled?: boolean;
}

export function useEventFormChatHelpers({
  sessionId,
  activeField,
  formState,
  summary,
  descriptionAutoGenerated = false,
  onResponse,
  onUserMessage,
  onError,
  onReset,
  onEdit,
  onCloseAssistant,
  modelTargetId,
  enabled = true,
}: UseEventFormChatOptions) {
  const productBase = useProductAnalyticsBase();
  const terreiroId = useCachedTerreiro()?.id ?? "";
  const { hasFeature } = useSubscription();
  const { submitEventByChat } = useFormChatSubmitEvent();
  const {
    items = [],
    mediuns = [],
    activities = [],
    addresses,
  } = useCreateEventQueries(terreiroId, undefined, { enabled });

  const isTerreiroAddressEmpty = !isTerreiroAddressConfigured(addresses);

  const activityOptions = useMemo(
    () =>
      (activities as Array<Record<string, unknown>>).map((activity) => ({
        id: String(activity.id),
        title: String(activity.title ?? ""),
        description: String(activity.description ?? ""),
        status: String(activity.status ?? ""),
      })),
    [activities],
  );

  const selectedActivityIds = useMemo(
    () =>
      ((formState.activities as Array<{ id: string }> | undefined) ?? []).map(
        (a) => a.id,
      ),
    [formState.activities],
  );

  const mergedMembers = useMemo(
    () =>
      buildMergedMediums(
        selectedActivityIds,
        activities as never[],
        mediuns as never[],
      ),
    [activities, mediuns, selectedActivityIds],
  );

  const mergedInsumos = useMemo(
    () =>
      buildMergedInsumos(
        selectedActivityIds,
        activities as never[],
        (items as Array<Record<string, unknown>>).map((item) => ({
          id: String(item.id),
          name: String(item.name ?? ""),
          quantity: toSafeInventoryQuantity(item.quantity),
          unit: String(item.unit ?? ""),
          imageUrl: item.imageUrl as string | undefined,
        })),
      ),
    [activities, items, selectedActivityIds],
  );

  const memberFromActivityMap = useMemo(
    () =>
      Object.fromEntries(
        mergedMembers
          .filter((m: EventMergedMediumOption) => m.fromActivity)
          .map((m: EventMergedMediumOption) => [m.id, true]),
      ),
    [mergedMembers],
  );

  const insumoFromActivityMap = useMemo(
    () =>
      Object.fromEntries(
        mergedInsumos
          .filter((i: EventMergedInsumoOption) => i.fromActivity)
          .map((i: EventMergedInsumoOption) => [String(i.id), true]),
      ),
    [mergedInsumos],
  );

  const lockedMemberIds = useMemo(
    () =>
      mergedMembers
        .filter((m: EventMergedMediumOption) => m.fromActivity)
        .map((m: EventMergedMediumOption) => m.id),
    [mergedMembers],
  );

  const lockedInsumoIds = useMemo(
    () =>
      mergedInsumos
        .filter((i: EventMergedInsumoOption) => i.fromActivity)
        .map((i: EventMergedInsumoOption) => String(i.id)),
    [mergedInsumos],
  );

  const eventReferenceData = useMemo<EventReferenceData>(
    () => ({
      members: mergedMembers.map((m: EventMergedMediumOption) => ({ id: m.id, name: m.name })),
      insumos: mergedInsumos.map((i: EventMergedInsumoOption) => ({
        id: String(i.id),
        name: i.name,
        availableQuantity: i.availableQuantity,
        unit: i.unit,
      })),
      activities: activityOptions,
      eventTypes: EVENT_TYPE_OPTIONS,
      canCreateTemplates: hasFeature("canUseTemplates"),
      isTerreiroAddressEmpty,
    }),
    [
      activityOptions,
      hasFeature,
      isTerreiroAddressEmpty,
      mergedInsumos,
      mergedMembers,
    ],
  );

  const { mutateAsync: sendEventMutation, isPending } = useMutation({
    mutationKey: ["form-chat", "event-message"],
    mutationFn: async (
      payload: Omit<FormChatMessageRequest, "entityType">,
    ): Promise<FormChatMessageResponse> => {
      if (!terreiroId) throw new Error("Terreiro context required");
      return formChatRepository.sendMessage(terreiroId, {
        entityType: "event",
        referenceData: eventReferenceData,
        ...(modelTargetId
          ? { mode: "from_model" as const, targetEntityId: modelTargetId }
          : {}),
        ...payload,
      });
    },
  });

  const sendEventRequest = useCallback(
    async (
      payload: Omit<FormChatMessageRequest, "entityType" | "referenceData">,
    ) => {
      try {
        const response = await sendEventMutation(payload);
        onResponse(response);
        return response;
      } catch (err: unknown) {
        const nextError = extractAxiosApiError(err);
        onError?.(nextError);
        trackEventsError("event_form_chat_submit_error", productBase, err);
        throw err;
      }
    },
    [onError, onResponse, productBase, sendEventMutation],
  );

  const handleStructuredSelection = useCallback(
    async (
      field: EventStructuredSelectionPayload["field"],
      value: unknown,
    ) => {
      let structuredSelection: EventStructuredSelectionPayload;

      if (field === "memberIds") {
        const payload = value as EventChatMembersConfirmPayload;
        const memberIds = [...new Set([...payload.memberIds, ...lockedMemberIds])];
        structuredSelection = {
          field: "memberIds",
          memberIds,
          minConfirmedMembers: quorumForMemberSelection(
            memberIds,
            payload.minConfirmedMembers,
          ),
        };
      } else if (field === "insumos") {
        structuredSelection = {
          field: "insumos",
          insumos: value as Array<{ id: string; quantity: number }>,
        };
      } else if (field === "activities") {
        const payload = value as {
          activities: Array<{ id: string; startTime?: string; endTime?: string; required?: boolean }>;
          requiredActivityIds: string[];
        };
        structuredSelection = {
          field: "activities",
          activities: payload.activities,
          requiredActivityIds: payload.requiredActivityIds,
        };
      } else if (field === "location") {
        structuredSelection = {
          field: "location",
          location: value as EventStructuredSelectionPayload["location"],
        };
      } else if (field === "type") {
        structuredSelection = {
          field: "type",
          type: value as string,
        };
      } else {
        return;
      }

      const userLabel = buildStructuredSelectionLabel(field, value);
      if (userLabel) {
        onUserMessage(userLabel);
      }

      await sendEventRequest({
        sessionId,
        message: "",
        structuredSelection,
      });
    },
    [lockedMemberIds, onUserMessage, sendEventRequest, sessionId],
  );

  const confirmLockRef = useRef(false);
  const [isConfirming, setIsConfirming] = useState(false);

  const handleConfirmEvent = useCallback(async () => {
    if (confirmLockRef.current) return;
    confirmLockRef.current = true;
    setIsConfirming(true);
    try {
      let mergedState = normalizeEventFormChatState({
        data: formState,
        terreiroId,
      });

      if (sessionId) {
        try {
          const response = await sendEventRequest({
            sessionId,
            message: "",
            action: "confirm",
          });
          if (response?.formState) {
            mergedState = normalizeEventFormChatState({
              data: { ...mergedState, ...response.formState },
              terreiroId,
            });
          }
        } catch {
          // Continue with client state.
        }
      }

      const payload = buildEventFormChatPayload({
        data: mergedState,
        terreiroId,
        memberFromActivityMap,
        insumoFromActivityMap,
      });

      if (!payload) {
        onError?.({
          code: "FORM_CHAT_INCOMPLETE",
          message: "Preencha nome e tipo do evento antes de criar.",
        });
        return;
      }

      if (!payload.isTemplate && !payload.date) {
        onError?.({
          code: "FORM_CHAT_INCOMPLETE",
          message: "Informe a data do evento antes de criar.",
        });
        return;
      }

      await submitEventByChat({
        data: mergedState,
        memberFromActivityMap,
        insumoFromActivityMap,
        modelId: modelTargetId,
      });
      onReset();
      onCloseAssistant?.();
    } catch (err: unknown) {
      onError?.(
        extractAxiosApiError(
          err,
          "Não foi possível criar o evento. Revise os dados e tente novamente.",
        ),
      );
    } finally {
      confirmLockRef.current = false;
      setIsConfirming(false);
    }
  }, [
    formState,
    insumoFromActivityMap,
    memberFromActivityMap,
    modelTargetId,
    onError,
    onReset,
    onCloseAssistant,
    sendEventRequest,
    sessionId,
    submitEventByChat,
    terreiroId,
  ]);

  const fieldPanel = useMemo(() => {
    if (activeField === "type") {
      return (
        <EventChatTypePicker
          options={EVENT_TYPE_OPTIONS}
          selectedId={formState.type as string | undefined}
          isLoading={isPending}
          onConfirm={(typeId: string) => {
            void handleStructuredSelection("type", typeId);
          }}
        />
      );
    }
    if (activeField === "location") {
      return (
        <EventChatLocationPicker
          value={formState.location as never}
          isTerreiroAddressEmpty={isTerreiroAddressEmpty}
          isLoading={isPending}
          onConfirm={(location: EventStructuredSelectionPayload["location"]) =>
            handleStructuredSelection("location", location)
          }
        />
      );
    }
    if (activeField === "activities") {
      return (
        <EventChatActivitiesPicker
          activities={activityOptions}
          selected={(formState.activities as never) ?? []}
          isAllDay={formState.isAllDay}
          isTemplate={formState.isTemplate}
          isLoading={isPending}
          onConfirm={(payload: {
            activities: Array<{
              id: string;
              startTime?: string;
              endTime?: string;
              required?: boolean;
            }>;
            requiredActivityIds: string[];
          }) => handleStructuredSelection("activities", payload)}
        />
      );
    }
    if (activeField === "memberIds") {
      return (
        <EventChatMembersPicker
          members={mergedMembers}
          selectedIds={(formState.memberIds as string[]) ?? lockedMemberIds}
          lockedIds={lockedMemberIds}
          selectedMinConfirmedMembers={
            typeof formState.minConfirmedMembers === "number"
              ? formState.minConfirmedMembers
              : undefined
          }
          isLoading={isPending}
          onConfirm={(payload: EventChatMembersConfirmPayload) =>
            handleStructuredSelection("memberIds", payload)
          }
        />
      );
    }
    if (activeField === "insumos") {
      return (
        <EventChatInsumosPicker
          insumos={mergedInsumos}
          selected={(formState.insumos as Array<{ id: string; quantity: number }>) ?? []}
          lockedIds={lockedInsumoIds}
          isLoading={isPending}
          onConfirm={(insumos: Array<{ id: string; quantity: number }>) =>
            handleStructuredSelection("insumos", insumos)
          }
        />
      );
    }
    return null;
  }, [
    activeField,
    activityOptions,
    formState.activities,
    formState.insumos,
    formState.isAllDay,
    formState.isTemplate,
    formState.location,
    formState.memberIds,
    formState.minConfirmedMembers,
    formState.type,
    handleStructuredSelection,
    isPending,
    isTerreiroAddressEmpty,
    lockedInsumoIds,
    lockedMemberIds,
    mergedInsumos,
    mergedMembers,
  ]);

  const editableFieldOptions = useMemo<FormChatEditableFieldOption[]>(() => {
    const read = (key: keyof EventChatSummary) =>
      summary?.[key] ?? formState[key as keyof FormChatState];

    const members = toStringList(read("memberIds")).map((id, index) => {
      const option = mergedMembers.find((member: EventMergedMediumOption) => member.id === id);
      return {
        name: option?.name ?? summary?.memberNames?.[index] ?? "Participante",
        src: option?.profilePictureUrl ?? null,
      };
    });

    const insumos = toInsumoSelection(read("insumos")).map((selection, index) => {
      const option = mergedInsumos.find(
        (item: EventMergedInsumoOption) => String(item.id) === selection.id,
      );
      const name = option?.name ?? summary?.insumoLabels?.[index] ?? "Material";
      return {
        name,
        label: option ? `${name} (${selection.quantity})` : name,
        src: option?.imageUrl ?? null,
      };
    });

    const activityTitles = (
      (read("activities") as Array<{ id: string }> | undefined) ?? []
    ).map(
      (selection, index) =>
        activityOptions.find((activity) => activity.id === selection.id)?.title ||
        summary?.activityLabels?.[index] ||
        "Atividade",
    );

    const type = read("type");
    const typeLabel =
      typeof type === "string"
        ? type === "OTHER" && typeof read("typeCustomLabel") === "string"
          ? String(read("typeCustomLabel"))
          : EVENT_TYPE_OPTIONS.find((option) => option.value === type)?.label ?? type
        : undefined;

    const location = read("location") as EventChatLocationState | undefined;
    const isExternal =
      read("isInTerreiro") === false ||
      location?.isInTerreiro === false ||
      (isTerreiroAddressEmpty && Boolean(location?.street));
    const locationLabel = isExternal
      ? formatLocationLabel({ ...location, isInTerreiro: false })
      : read("isInTerreiro") === true || location
        ? formatLocationLabel(location)
        : undefined;

    const qrCheckinEnabled = read("qrCheckinEnabled");

    const currentValueByKey: Record<string, string | undefined> = {
      name: formatChatText(read("name")),
      type: typeLabel,
      description: formatChatText(read("description")),
      isTemplate: formatChatYesNo(read("isTemplate")),
      date: formatChatDate(read("date")),
      startTime: formatChatTime(read("startTime")),
      endTime: formatChatTime(read("endTime")),
      location: locationLabel || undefined,
      qrCheckinEnabled: formatChatToggle(qrCheckinEnabled),
      checkinStartWindowMinutes: formatChatMinutes(read("checkinStartWindowMinutes")),
      checkinEndWindowMinutes: formatChatMinutes(read("checkinEndWindowMinutes")),
      activities: activityTitles.length ? activityTitles.join(", ") : undefined,
      memberIds: members.length
        ? members.map((member) => member.name).join(", ")
        : undefined,
      minConfirmedMembers:
        members.length === 0
          ? undefined
          : formatChatText(read("minConfirmedMembers")),
      insumos: insumos.length
        ? insumos.map((insumo) => insumo.label).join(", ")
        : undefined,
    };

    return EVENT_EDITABLE_FIELDS.filter(
      (field) =>
        !(modelTargetId && field.key === "isTemplate") &&
        (qrCheckinEnabled === true || !CHECKIN_WINDOW_EDIT_FIELDS.has(field.key)),
    ).map((field) => ({
      key: field.key,
      label: field.label,
      currentValue: currentValueByKey[field.key],
      avatars:
        field.key === "memberIds"
          ? members.map((member) => ({ src: member.src, alt: member.name }))
          : field.key === "insumos"
            ? insumos.map((insumo) => ({ src: insumo.src, alt: insumo.name }))
            : undefined,
    }));
  }, [
    activityOptions,
    formState,
    isTerreiroAddressEmpty,
    mergedInsumos,
    mergedMembers,
    modelTargetId,
    summary,
  ]);

  const summaryCard = useMemo(() => {
    if (!summary) return null;
    return (
      <EventFormChatSummaryCard
        summary={summary}
        confirmLabel="Criar evento"
        isLoading={isPending || isConfirming}
        descriptionAutoGenerated={descriptionAutoGenerated}
        onConfirm={handleConfirmEvent}
        onEdit={onEdit}
      />
    );
  }, [descriptionAutoGenerated, handleConfirmEvent, isConfirming, isPending, onEdit, summary]);

  return {
    eventReferenceData,
    sendEventRequest,
    handleConfirmEvent,
    fieldPanel,
    summaryCard,
    stepOrder: EVENT_STEP_ORDER,
    editableFields: EVENT_EDITABLE_FIELDS,
    editableFieldOptions,
    isPending,
  };
}